import {createSelector} from "reselect";

import {
    getArticleOptionsData,
    getBaseArticle,
    getCartBackupRange,
    getCartBillingAddress,
    getCartContactPerson,
    getCartContractPartner,
    getCartItems,
    getCartNote,
    getCartPaymentSettings,
    getCartPromotionCode,
    getCartRecommendedAccount,
    getCartSoftware,
    getDomainData,
    getPromotionCodeInformation,
    isPromotionCodeValid
} from "./SimpleSelectors";

import {getArticleOptionsInCartList, getArticleOptionsInCartObject, getDomainInCartObject} from './CartUtil'
import {getSoftwareData} from "./Software";
import {delve} from "../util/util";
import {getAppMode} from "./Mode";
import {APP_MODE_AUTHENTICATED_ORDER, APP_MODE_REGULAR} from "../constants/General";
import {accessoryArticles, agencyServerArticleNumber, agencyServerProjectArticles, cmsArticles, dsgvoArticles, shopArticles} from "../config/Config";

export * from './CartUtil'


export const getFirstArticleData = createSelector(
    [getCartItems, getArticleOptionsData, getBaseArticle],
    (cartItems, articleOptions, baseArticle) => {

        const firstArticleNumber = getMainArticleByItems(cartItems);

        if (baseArticle && (baseArticle.articleNumber + '' === firstArticleNumber)) {

            return baseArticle;

        } else if (articleOptions && articleOptions[firstArticleNumber]) {

            return articleOptions[firstArticleNumber];

        } else {

            return null;

        }
    }
)

/**
 * Gets the billing period value that is or is supposed to be set as cart property. Mittwald values are transformed back to the mapped value.
 */
export const getCartBillingPeriod = createSelector(
    [
        state => state.cart && state.cart.billingPeriod,
        getFirstArticleData
    ],
    (cartBillingPeriod, firstArticleData) => {

        const billingPeriodMappingReverse = {
            0: 0,
            1: 1,
            3: 2,
            6: 3,
            12: 4
        }

        if (cartBillingPeriod) {

            return cartBillingPeriod;

        } else {


            if (firstArticleData) {

                if (Number(delve(firstArticleData, "billing.billingPeriod")) >= 0) {

                    return billingPeriodMappingReverse[firstArticleData.billing.billingPeriod] >= 0 ? billingPeriodMappingReverse[firstArticleData.billing.billingPeriod] : 12;

                } else {

                    // mapping for 12 months
                    return 4;

                }

            } else {

                // mapping for 12 months
                return 4;

            }


        }

    }
)

// **************************************************************************************************************
export const getItemInCartByArticleNumber = (state, articleNumber) => {

    const cartItems = getCartItems(state)

    if (!cartItems) {
        return false
    }

    const itemToRemove = cartItems.find((item) => item.identifier === ("" + articleNumber))

    return itemToRemove ? itemToRemove.id : false

}


const getTrimmedBaseArticle = (baseArticleData) => {

    let val;

    const appMode = getAppMode();
    // only show setup fee for new orders
    const hasSetupFee = appMode === APP_MODE_REGULAR || appMode === APP_MODE_AUTHENTICATED_ORDER;

    if (Object.keys(baseArticleData).length > 0) {
        val = {
            title: baseArticleData.title,
            type: baseArticleData.type,
            description: baseArticleData.description,
            setupFee: hasSetupFee ? baseArticleData.price.default.setupFee : 0,
            priceMonthly: baseArticleData.price.default.net,
            articleNumber: baseArticleData.articleNumber,
            billingPeriod: delve(baseArticleData, "billing.billingPeriod"),
            billingPeriodUnlocked: delve(baseArticleData, "billing.allowDurationModification"),
        }
    } else {
        val = {
            title: "",
            description: "",
            setupFee: 0,
            priceMonthly: 0,
            articleNumber: false,
            billingPeriod: 12,
            billingPeriodUnlocked: false
        }
    }

    if (delve(baseArticleData, "billing.billingPeriod") === 0) {
        val.setupFee = val.priceMonthly;
        val.priceMonthly = 0;
    }


    return val;

}


export const getRemainingInclusiveDomains = createSelector(
    [getCartItems, getDomainData, getBaseArticle],
    (cartItems, domainData, baseArticle) => {

        let limit = baseArticle && baseArticle.benefits && baseArticle.benefits.domain && baseArticle.benefits.domain.inclusive

        const domainItems = cartItems.filter((cartItem) => {
            return cartItem.itemType === "DOMAIN"
        })

        domainItems.map((domainItem) => {
            const split = domainItem.identifier.split(/\.([a-zA-Z.]+)/)
            if (domainData[split[1]].isAllowedAsInclusiveDomain !== 0) {
                limit--
            }
            return null
        })

        return limit

    }
)

export const isDevProjectInCart = createSelector(
    [getArticleOptionsInCartList],
    (articleOptionsInCartList) => {
        let isDevProjectInCart = false;

        articleOptionsInCartList.forEach(articleOptionNumber => {
            if (articleOptionNumber + "" === "72002") {
                isDevProjectInCart = true;
            }
        })

        return isDevProjectInCart;
    }
)

export const getPromotionsInCart = createSelector(
    [
        getPromotionCodeInformation,
        isPromotionCodeValid,
        getBaseArticle,
        getArticleOptionsInCartObject
    ],
    (promotionCodeInformation,
     isPromotionCodeValid,
     baseArticle,
     articleOptions) => {

        // base article data
        const baseArticleTrimmed = getTrimmedBaseArticle(baseArticle);

        // manage promotions
        let promotions = []

        // add base article promotion
        if (isPromotionCodeValid && promotionCodeInformation && promotionCodeInformation.articles) {
            if (promotionCodeInformation.articles.includes(baseArticle.articleNumber)) {
                promotions.push(
                    {
                        code: promotionCodeInformation.code,
                        articleNumber: baseArticle.articleNumber,
                        title: "Aktion " + promotionCodeInformation.code.toUpperCase() + ": " + baseArticleTrimmed.title,
                        reductionSetupFee: !promotionCodeInformation.periodic ? (promotionCodeInformation.absolute ? promotionCodeInformation.discount : (baseArticleTrimmed.setupFee * promotionCodeInformation.discount)) : 0,
                        reductionPriceMonthly: promotionCodeInformation.periodic ? (promotionCodeInformation.absolute ? promotionCodeInformation.discount : (baseArticleTrimmed.priceMonthly * promotionCodeInformation.discount)) : 0,
                        freeRuntime: promotionCodeInformation.runtime ? Number(promotionCodeInformation.freeRuntime) : 0
                    }
                )
            }
            articleOptions.forEach((articleOption) => {

                if (promotionCodeInformation.articles.includes(articleOption.articleNumber)) {
                    promotions.push(
                        {
                            code: promotionCodeInformation.code,
                            articleNumber: articleOption.articleNumber,
                            title: "Aktion " + promotionCodeInformation.code.toUpperCase() + ": " + articleOption.title,
                            reductionSetupFee: !promotionCodeInformation.periodic ? (promotionCodeInformation.absolute ? promotionCodeInformation.discount : (articleOption.setupFee * promotionCodeInformation.discount)) : 0,
                            reductionPriceMonthly: promotionCodeInformation.periodic ? (promotionCodeInformation.absolute ? promotionCodeInformation.discount : (articleOption.priceMonthly * promotionCodeInformation.discount)) : 0
                        }
                    )
                }
            })
        }

        return promotions

    }
)


export const getTotalCartCosts = createSelector(
    [getDomainInCartObject, getBaseArticle, getCartPaymentSettings, getPromotionsInCart, getArticleOptionsInCartObject, getCartItems],
    (domainItems, baseArticleData, paymentSettings, promotionsInCart, articleOptions, cartItems) => {

        if (!baseArticleData) {
            return {
                setupFee: 0,
                priceMonthly: 0
            }
        }

        const baseArticleTrimmed = getTrimmedBaseArticle(baseArticleData);

        let setupFee = 0
        let priceMonthly = 0
        let freeRuntime = 0

        const baseArticleInCart = cartItems ? cartItems.some(item => {
            return (item.identifier === "" + baseArticleTrimmed.articleNumber)
        }) : false;

        if (baseArticleInCart) {
            // add base article
            priceMonthly += baseArticleTrimmed.priceMonthly
            setupFee += baseArticleTrimmed.setupFee
        }

        // add article options
        let articlePrices = articleOptions.reduce((a, b) => (
            {
                setupFee: Number(a.setupFee) + Number(b.setupFee),
                priceMonthly: Number(a.priceMonthly) + Number(b.priceMonthly)
            }
        ), {setupFee: 0, priceMonthly: 0})

        // add domains
        const domainPrices = domainItems.reduce((a, b) => (
            {
                setupFee: Number(a.setupFee) + Number(b.setupFee),
                priceMonthly: Number(a.priceMonthly) + Number(b.priceMonthly)
            }
        ), {setupFee: 0, priceMonthly: 0})

        //postal invoice
        if (paymentSettings && paymentSettings.postalInvoice) {
            setupFee += 1
        }

        // promotion code data
        promotionsInCart.forEach(promotionData => {
            setupFee -= promotionData.reductionSetupFee
            priceMonthly -= promotionData.reductionPriceMonthly
            freeRuntime += Number(promotionData.freeRuntime)
        })

        return {
            setupFee: Number(setupFee) + Number(domainPrices.setupFee) + Number(articlePrices.setupFee),
            priceMonthly: Number(priceMonthly) + Number(domainPrices.priceMonthly + Number(articlePrices.priceMonthly)),
            domainPricesMonthly: Number(domainPrices.priceMonthly),
            freeRuntime: freeRuntime
        }

    }
)

export const getBaseArticlePrice = createSelector(
    [getBaseArticle],
    (baseArticle) => {
        return baseArticle && baseArticle.price ? baseArticle.price.default.net : 0
    }
)

export const getCartSummary = createSelector(
    [
        getBaseArticle,
        getCartSoftware,
        getSoftwareData,
        getArticleOptionsInCartObject,
        getDomainInCartObject,
        getCartPaymentSettings,
        getPromotionsInCart,
        getTotalCartCosts,
        getCartBillingPeriod,
        getCartBackupRange,
        getCartItems
    ],
    (baseArticle,
     softwareSelected,
     softwareData,
     articleOptions,
     domains,
     paymentSettings,
     promotionsInCart,
     totalCartCosts,
     billingPeriod,
     backupRange,
     cartItems
    ) => {

        // base article data
        const baseArticleTrimmed = getTrimmedBaseArticle(baseArticle);

        // postal invoice
        const postalInvoice = (paymentSettings && paymentSettings.postalInvoice) ? {
            priceMonthly: 0,
            setupFee: 1
        } : false

        const billingPeriodMapping = {
            0: 0,
            1: 1,
            2: 3,
            3: 6,
            4: 12
        }

        const billingPeriodMonths = billingPeriodMapping[billingPeriod];

        let firstInvoiceSum = billingPeriodMonths * totalCartCosts.priceMonthly + totalCartCosts.setupFee

        if (billingPeriodMonths < 12) {
            // normalize domain price to 12 months
            firstInvoiceSum += totalCartCosts.domainPricesMonthly * (12 - billingPeriodMonths)
        }

        let freeMonthsEffective = 0

        if (totalCartCosts.freeRuntime > 0) {
            freeMonthsEffective = Math.min(totalCartCosts.freeRuntime, billingPeriodMonths);
        }

        if (baseArticleTrimmed && baseArticleTrimmed.priceMonthly) {

            firstInvoiceSum -= freeMonthsEffective * (baseArticleTrimmed.priceMonthly)

        }


        const baseArticleInCart = cartItems ? cartItems.some(item => {
            return (item.identifier === "" + baseArticle.articleNumber)
        }) : false;

        return {
            baseArticle: baseArticleTrimmed,
            software: softwareData && softwareData[softwareSelected] ? softwareData[softwareSelected] : false,
            articleOptions,
            domains,
            cartItems,
            paymentSettings: Object.assign({}, paymentSettings, {postalInvoice}),
            promotions: promotionsInCart,
            total: totalCartCosts,
            firstInvoiceSum,
            billingPeriodMonths,
            freeMonthsEffective,
            backupRange,
            baseArticleInCart: baseArticleInCart
        }
    }
)

export const getInitialCartFormData = createSelector(
    [getCartContractPartner, getCartBillingAddress, getCartContactPerson, getCartRecommendedAccount, getCartNote, getCartPromotionCode, getCartPaymentSettings],
    (contractPartner, billingAddress, contactPerson, recommendedAccount, orderNote, promotionCode, paymentSettings) => {
        return {
            contractPartner: contractPartner,
            billingAddress: billingAddress,
            contactPerson: contactPerson,
            recommendedAccount: recommendedAccount,
            note: orderNote,
            promotionCode: promotionCode,
            paymentSettings: paymentSettings
        }
    }
)

export const getMainArticleByItems = (cartItems) => {
    if (!cartItems) {
        return '';
    }
    const mainArticles = [
        agencyServerArticleNumber,
        ...cmsArticles,
        ...shopArticles,
        ...agencyServerProjectArticles,
        ...dsgvoArticles,
        ...accessoryArticles,
        '30000'
    ]
    const mainArticle = cartItems.find((element) => {
        return mainArticles.indexOf(element.identifier) >= 0;
    });
    return mainArticle && mainArticle.identifier;
}


export const getMainArticleInCart = createSelector(
    [getCartItems],
    (cartItems) => {
        return getMainArticleByItems(cartItems);
    }
)



/** WEBPACK FOOTER **
 ** ./src/selectors/Cart.js
 **/